import { buildSessionReport, dateTime, loadReportLogo, reportColors, reportFilename } from './posSessionReport';

export function safeCell(value: unknown): unknown {
    if (typeof value !== 'string') return value;
    return /^[=+\-@]/.test(value) ? "'" + value : value;
}

export async function buildExcelSessionReport(data: any) {
    const ExcelJS = await import('exceljs');
    const wb = new ExcelJS.Workbook();
    wb.creator = 'Co Marca Group'; wb.created = new Date();
    const report = buildSessionReport(data);
    const { session } = report;
    const logo = await loadReportLogo();
    const imageId = logo ? wb.addImage({ base64: logo, extension: 'png' }) : null;
    const names: Record<string, string> = { 'Registro y conciliación de caja': 'Resumen de caja', 'Actividad de la sesión': 'Actividad', 'Historial completo de pagos': 'Historial de pagos' };
    report.sections.forEach((section, index) => {
        const ws = wb.addWorksheet(section.sheetName || names[section.title] || section.title.slice(0, 31), {
            views: [{ state: 'frozen', ySplit: 10, showGridLines: false }],
            pageSetup: { paperSize: 9, orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0, printTitlesRow: '1:10' }
        });
        const cols = Math.max(4, section.headers.length);
        ws.columns = Array.from({ length: cols }, (_, col) => ({ width: col === 0 ? (section.headers.length === 2 ? 62 : 30) : col === 1 ? 34 : 26 }));
        ws.mergeCells(1, 1, 1, cols - 1);
        ws.getCell('A1').value = 'CO MARCA GROUP';
        ws.getCell('A1').font = { name: 'Calibri', size: 18, bold: true, color: { argb: 'FF0F172A' } };
        ws.mergeCells(2, 1, 2, cols - 1);
        ws.getCell('A2').value = section.title;
        ws.getCell('A2').font = { size: 14, bold: true, color: { argb: 'FF007FC8' } };
        ws.getRow(1).height = 30; ws.getRow(2).height = 26;
        if (imageId != null) ws.addImage(imageId, { tl: { col: cols - 1, row: 0 }, ext: { width: 68, height: 68 } });
        for (let col = 1; col <= cols; col++) {
            ws.getCell(4, col).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF' + reportColors[Math.floor((col - 1) * 4 / cols)] } };
        }
        ws.getRow(4).height = 4;
        const metadata = [
            ['Sesión / estado', String(session.codigo || session.id || '-') + ' / ' + (report.closed ? 'Cerrada' : 'Abierta - provisional')],
            ['POS / sucursal', String(session.punto_de_venta_nombre || session.punto_de_venta || '-') + ' / ' + String(session.sucursal_nombre || '-') + ' · ' + report.typeLabel],
            ['Responsable', String(session.usuario_nombre || session.usuario || '-')],
            ['Apertura / cierre', dateTime(session.fecha_apertura) + ' / ' + (report.closed ? dateTime(session.fecha_cierre) : 'Pendiente')]
        ];
        metadata.forEach(([label, value], r) => {
            ws.getCell(r + 5, 1).value = label;
            ws.getCell(r + 5, 1).font = { bold: true, color: { argb: 'FF475569' } };
            ws.mergeCells(r + 5, 2, r + 5, cols);
            ws.getCell(r + 5, 2).value = safeCell(value) as string;
            ws.getCell(r + 5, 2).alignment = { wrapText: true, vertical: 'middle' };
            ws.getRow(r + 5).height = 27;
        });
        ws.getRow(10).values = section.headers;
        ws.getRow(10).height = 32;
        ws.getRow(10).eachCell(cell => {
            cell.font = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FFFFFFFF' } };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF007FC8' } };
            cell.alignment = { wrapText: true, vertical: 'middle' };
        });
        const rows = section.rows.length ? section.rows : [['Sin registros en esta sesión']];
        rows.forEach((values, r) => {
            const row = ws.getRow(r + 11);
            row.values = values.map(value => safeCell(value) as string | number);
            row.height = Math.max(30, ...values.map((v, c) => 15 * (String(v).split('\n').reduce((n, line) => n + Math.max(1, Math.ceil(line.length / ((ws.getColumn(c + 1).width || 26) - 2))), 0) + 1)));
            for (let col = 1; col <= section.headers.length; col++) {
                const cell = row.getCell(col);
                cell.font = { name: 'Calibri', size: 11, color: { argb: 'FF1E293B' } };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: r % 2 ? 'FFFFFFFF' : 'FFF1F5F9' } };
                cell.alignment = { wrapText: true, vertical: 'middle', horizontal: typeof cell.value === 'number' ? 'right' : 'left' };
                cell.border = { bottom: { style: 'hair', color: { argb: 'FFE2E8F0' } } };
                if (section.moneyColumns?.includes(col - 1) && typeof cell.value === 'number') cell.numFmt = '"Bs" #,##0.00;[Red]"Bs" -#,##0.00';
            }
        });
        if (section.rows.length) ws.autoFilter = { from: { row: 10, column: 1 }, to: { row: 10 + section.rows.length, column: section.headers.length } };
        const noteRow = 12 + rows.length;
        ws.mergeCells(noteRow, 1, noteRow, cols);
        ws.getCell(noteRow, 1).value = index === 0
            ? 'Caja = saldo inicial + entradas - salidas. Las entradas por pagos ya están incluidas en los movimientos; no se suman dos veces. Banco / tarjeta se informa por separado.'
            : 'Datos de esta sesión POS (' + report.typeLabel + '). Los registros cancelados se conservan para mantener el historial.';
        ws.getCell(noteRow, 1).alignment = { wrapText: true, vertical: 'middle' };
        ws.getCell(noteRow, 1).font = { size: 10, italic: true, color: { argb: 'FF475569' } };
        ws.getRow(noteRow).height = 36;
        ws.headerFooter.oddFooter = '&LCo Marca Group&CReporte de sesión POS&RPágina &P de &N';
    });
    return wb;
}

export async function generarExcelReporteSesionPOS(data: any) {
    const wb = await buildExcelSessionReport(data);
    const buffer = await wb.xlsx.writeBuffer();
    const url = URL.createObjectURL(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
    const link = document.createElement('a');
    link.href = url; link.download = reportFilename(data.sesion || {}, 'xlsx');
    document.body.appendChild(link); link.click(); link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}
